import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, Loader2 } from "lucide-react";
import { useT } from "@/lib/translation";

interface TemplateHelpModalProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onDownloadTemplate?: () => void;
  isDownloadingTemplate?: boolean;
}

const TemplateHelpModal = ({
  isOpen,
  onOpenChange,
  onDownloadTemplate,
  isDownloadingTemplate,
}: TemplateHelpModalProps) => {
  const t = useT();
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto [&>button:focus]:ring-0 [&>button:focus]:ring-offset-0">
        <DialogHeader>
          <DialogTitle>{t("import:templateHelp.title")}</DialogTitle>
        </DialogHeader>
        {onDownloadTemplate && (
          <Button
            size="sm"
            className="w-fit gap-2"
            onClick={onDownloadTemplate}
            disabled={isDownloadingTemplate}
          >
            {isDownloadingTemplate ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Download className="h-4 w-4" />
            )}
            <span>{t("import:templateHelp.download")}</span>
          </Button>
        )}
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("import:templateHelp.quickStart.title")}
            </h3>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>{t("import:templateHelp.quickStart.step1")}</li>
              <li>{t("import:templateHelp.quickStart.step2")}</li>
              <li>{t("import:templateHelp.quickStart.step3")}</li>
              <li>{t("import:templateHelp.quickStart.step4")}</li>
            </ol>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("import:templateHelp.required.title")}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-2 text-left">
                      {t("import:templateHelp.required.colColumn")}
                    </th>
                    <th className="border border-border p-2 text-left">
                      {t("import:templateHelp.required.colDescription")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-2 font-mono">date</td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.date")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">type</td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.type")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      ticker
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.ticker")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">isin</td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.isin")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono align-top">
                      quantity
                    </td>
                    <td className="border border-border p-2 align-top">
                      <div className="space-y-1">
                        <div>
                          {t("import:templateHelp.required.quantityShares")}
                        </div>
                        <div>
                          {t("import:templateHelp.required.quantityAmount")}
                        </div>
                        <div>
                          {t("import:templateHelp.required.quantityOptional")}
                        </div>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      price_native
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.price_native")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      currency_native
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.currency_native")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      settlement_currency
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.settlement_currency")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      settlement_amount
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.settlement_amount")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      fee_native
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.fee_native")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      fee_currency
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.fee_currency")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      base_to_native_fx_rate
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.base_to_native_fx_rate")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      from_currency
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.from_currency")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      from_amount
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.from_amount")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      to_currency
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.to_currency")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      to_amount
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.to_amount")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      withholding_tax_native
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.withholding_tax_native")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      source_transaction_id
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.source_transaction_id")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      notes
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.notes")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      exchange
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.required.exchange")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("import:templateHelp.types.title")}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse border border-border">
                <thead>
                  <tr className="bg-muted">
                    <th className="border border-border p-2 text-left">
                      {t("import:templateHelp.types.colType")}
                    </th>
                    <th className="border border-border p-2 text-left">
                      {t("import:templateHelp.types.colRequired")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      BUY / SELL
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.buySell")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      CONVERSION
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.conversion")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      DEPOSIT / WITHDRAWAL
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.cash")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      DIVIDEND
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.dividend")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      INTEREST
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.interest")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">FEE</td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.fee")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      TRANSFER_IN / TRANSFER_OUT
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.transfer")}
                    </td>
                  </tr>
                  <tr>
                    <td className="border border-border p-2 font-mono">
                      REWARD
                    </td>
                    <td className="border border-border p-2">
                      {t("import:templateHelp.types.reward")}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("import:templateHelp.validation.title")}
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>{t("import:templateHelp.validation.dates")}</li>
              <li>{t("import:templateHelp.validation.types")}</li>
              <li>{t("import:templateHelp.validation.numbers")}</li>
              <li>{t("import:templateHelp.validation.currencies")}</li>
              <li>{t("import:templateHelp.validation.fee")}</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("import:templateHelp.crypto.title")}
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>{t("import:templateHelp.crypto.exchange")}</li>
              <li>{t("import:templateHelp.crypto.staking")}</li>
              <li>{t("import:templateHelp.crypto.reward")}</li>
              <li>{t("import:templateHelp.crypto.transfer")}</li>
            </ul>
          </div>
          <div>
            <h3 className="text-lg font-semibold mb-3">
              {t("import:templateHelp.tips.title")}
            </h3>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>{t("import:templateHelp.tips.template")}</li>
              <li>{t("import:templateHelp.tips.formats")}</li>
              <li>{t("import:templateHelp.tips.empty")}</li>
              <li>{t("import:templateHelp.tips.errors")}</li>
              <li>{t("import:templateHelp.tips.comments")}</li>
            </ul>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TemplateHelpModal;
